/* ==========================================================================
   MIDOLO.ART — Global minimal music player
   Drives a YouTube playlist via the IFrame API. Audio-only (hidden iframe) on
   desktop/Android; a small visible mini-video on iOS (where hidden audio is
   blocked). Remembers track + position across page loads (one tap resumes).
   Minimises to a dot with a WebGPU red-particle burst; if WebGPU is missing
   the pill just collapses via CSS — the effect is purely additive.
   ========================================================================== */
(function () {
  "use strict";

  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var IOS = /iP(hone|od|ad)/.test(navigator.platform) ||
            /iPhone|iPad|iPod/.test(navigator.userAgent) ||
            (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  // In-app browsers (Instagram, Facebook, TikTok…) sandbox media far harder than Safari/Chrome.
  var INAPP = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|MicroMessenger|Line\/|TikTok|musical_ly|Snapchat|Pinterest/i.test(navigator.userAgent || "");
  var LS_KEY = "mmp:state";
  var YT_API = "https://www.youtube.com/iframe_api";
  var FEATURE_CINEMA_MAX = 4;

  function buildFeatureCinemaItems(order, startIndex) {
    var count = Math.min(FEATURE_CINEMA_MAX, order.length);
    var items = [];
    for (var i = 0; i < count; i++) {
      items.push(order[(startIndex + i) % order.length]);
    }
    return {
      items: items,
      nextIndex: startIndex + count
    };
  }
  function clearCinemaScene(scene) {
    if (!scene) return;
    scene.querySelectorAll(".mmp-cinema__pimg").forEach(function (image) {
      try {
        image.getAnimations().forEach(function (animation) { animation.cancel(); });
      } catch (e) {}
      image.style.willChange = "";
      image.removeAttribute("src");
    });
    scene.innerHTML = "";
  }
  if (typeof module !== "undefined" && module.exports) {
    module.exports.buildFeatureCinemaItems = buildFeatureCinemaItems;
    module.exports.clearCinemaScene = clearCinemaScene;
  }

  function loadYouTubeApi(cb) {
    if (window.YT && window.YT.Player) { cb(); return; }
    var prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prior === "function") { try { prior(); } catch (e) {} }
      cb();
    };
    if (!document.querySelector('script[src="' + YT_API + '"]')) {
      var s = document.createElement("script"); s.src = YT_API; s.async = true;
      document.head.appendChild(s);
    }
  }

  function loadState() { try { return JSON.parse(localStorage.getItem(LS_KEY)) || null; } catch (e) { return null; } }
  function saveState(st) { try { localStorage.setItem(LS_KEY, JSON.stringify(st)); } catch (e) {} }

  /* ---------- Player ------------------------------------------------------ */
  function initPlayer(root) {
    var playlist = (root.getAttribute("data-playlist") || "").trim();
    if (!playlist) return;
    root.hidden = false;
    if (IOS) root.classList.add("is-ios");

    var host = root.querySelector("[data-mmp-host]");
    var shell = root.querySelector("[data-mmp-shell]");
    var toggle = root.querySelector("[data-mmp-toggle]");
    var playBtn = root.querySelector("[data-mmp-play]");
    var prevBtn = root.querySelector("[data-mmp-prev]");
    var nextBtn = root.querySelector("[data-mmp-next]");
    var titleEl = root.querySelector("[data-mmp-title]");
    var thumbEl = root.querySelector("[data-mmp-thumb]");
    var minBtn = root.querySelector("[data-mmp-min]");
    var dotBtn = root.querySelector("[data-mmp-restore]");
    var fx = createFx(root.querySelector("[data-mmp-fx]"));

    var player = null, booted = false, ready = false, pendingAfter = [], startedOnce = false, playing = false, persistTimer = null, lastVid = null, cinema = null, watchT = null, hintShown = false, hintEl = null;
    var saved = loadState();

    if (saved && saved.title) setTitle(saved.title);
    if (saved && saved.min) root.classList.add("is-min"); // restore minimized state (no FX on load)
    // Gentle first-appearance entrance (skip if we loaded straight into the dot).
    if (!REDUCED && !root.classList.contains("is-min")) {
      root.classList.add("is-enter");
      setTimeout(function () { root.classList.remove("is-enter"); }, 520);
    }

    function setTitle(t) { if (titleEl && t) titleEl.textContent = t; }
    function measureTitle() {
      var outer = titleEl && titleEl.parentNode;
      if (!outer) return;
      outer.classList.remove("is-scroll");
      var avail = outer.clientWidth;
      if (avail < 20) return; // panel not open yet — nothing to measure
      var prevD = titleEl.style.display;
      titleEl.style.display = "inline-block";
      var full = titleEl.getBoundingClientRect().width;
      titleEl.style.display = prevD;
      var dist = Math.ceil(full - avail);
      if (!REDUCED && dist > 4) { // overflows → marquee it, else CSS ellipsis clips it
        outer.style.setProperty("--mmp-scroll-dist", dist + "px");
        outer.style.setProperty("--mmp-scroll-dur", Math.max(6, dist / 22).toFixed(1) + "s");
        outer.classList.add("is-scroll");
      }
    }
    function reflect() {
      if (!player || !player.getVideoData) return;
      var d = player.getVideoData();
      if (d && d.title) setTitle(d.title);
      if (d && d.video_id && thumbEl) thumbEl.src = "https://i.ytimg.com/vi/" + d.video_id + "/mqdefault.jpg";
      measureTitle();
      if (d && d.video_id && d.video_id !== lastVid) { lastVid = d.video_id; if (cinema) cinema.trackChanged(); }
    }
    function persist() {
      if (!player || !player.getPlaylistIndex) return;
      var d = player.getVideoData ? player.getVideoData() : null;
      saveState({
        index: player.getPlaylistIndex() || 0,
        time: player.getCurrentTime ? player.getCurrentTime() : 0,
        playing: playing,
        min: root.classList.contains("is-min"),
        title: (d && d.title) || (saved && saved.title) || ""
      });
    }
    function onState(e) {
      playing = e.data === 1; // YT.PlayerState.PLAYING
      if (playing) { clearTimeout(watchT); removeInAppHint(); } // real playback → drop any in-app hint (incl. a slow-start false positive)
      root.classList.toggle("is-playing", playing);
      if (playBtn) playBtn.setAttribute("aria-label", playing ? "Pausa" : "Play");
      reflect(); persist();
    }
    function bootPlayer(after) {
      if (booted) { if (after) { if (ready) after(); else pendingAfter.push(after); } return; }
      booted = true;
      setTitle("Caricamento…");
      var failT = setTimeout(function () { if (!player) root.hidden = true; }, 9000);
      var vars = { listType: "playlist", list: playlist, controls: IOS ? 1 : 0,
                   disablekb: 1, modestbranding: 1, rel: 0, playsinline: 1, fs: 0 };
      if (saved && typeof saved.index === "number") vars.index = saved.index;
      loadYouTubeApi(function () {
        player = new YT.Player(host, {
          width: IOS ? "116" : "1", height: IOS ? "66" : "1",
          playerVars: vars,
          events: {
            onReady: function () {
              clearTimeout(failT);
              ready = true;
              root.classList.add("is-booted");
              reflect();
              if (after) after();
              pendingAfter.splice(0).forEach(function (f) { try { f(); } catch (e) {} });
              if (!persistTimer) persistTimer = setInterval(function () { if (playing) persist(); }, 4000);
            },
            onStateChange: onState,
            onError: function () { if (player && player.nextVideo) player.nextVideo(); }
          }
        });
      });
    }
    function startPlayback() {
      if (!player) return;
      if (saved && saved.time && !startedOnce && player.seekTo) {
        try { player.seekTo(saved.time, true); } catch (e) {}
      }
      startedOnce = true;
      player.playVideo();
      // In-app browsers may silently refuse audio — if nothing is playing shortly, hint the user.
      if (INAPP) { clearTimeout(watchT); watchT = setTimeout(function () { if (!playing) showInAppHint(); }, 3200); }
    }
    function open() { root.classList.add("is-open"); toggle.setAttribute("aria-expanded", "true"); setTimeout(measureTitle, 560); }
    function close() { root.classList.remove("is-open"); toggle.setAttribute("aria-expanded", "false"); }

    toggle.addEventListener("click", function () {
      if (root.classList.contains("is-open")) { close(); return; }
      open();
      if (!startedOnce) bootPlayer(function () { startPlayback(); });
    });
    if (playBtn) playBtn.addEventListener("click", function () {
      if (!player) { bootPlayer(function () { startPlayback(); }); return; }
      if (playing) player.pauseVideo(); else startPlayback();
    });
    if (prevBtn) prevBtn.addEventListener("click", function () { if (player && player.previousVideo) player.previousVideo(); });
    if (nextBtn) nextBtn.addEventListener("click", function () { if (player && player.nextVideo) player.nextVideo(); });

    // In-app browsers (Instagram etc.) lose the user-gesture across the async YouTube boot and
    // then block audio. So pre-boot the player on the FIRST interaction; the later Play tap then
    // finds it ready and calls playVideo() synchronously, inside the gesture → audio allowed.
    if (INAPP) {
      var primeEvs = ["pointerdown", "touchstart", "keydown"];
      var prime = function () {
        primeEvs.forEach(function (ev) { document.removeEventListener(ev, prime, true); });
        if (!startedOnce) bootPlayer();
      };
      primeEvs.forEach(function (ev) { document.addEventListener(ev, prime, { capture: true, passive: true }); });
    }
    // Last resort: if the in-app browser still refuses audio, tell the user to open in a real browser.
    function removeInAppHint() {
      if (hintEl && hintEl.parentNode) hintEl.parentNode.removeChild(hintEl);
      hintEl = null;
    }
    function showInAppHint() {
      if (hintShown) return; hintShown = true;
      var h = document.createElement("div");
      h.setAttribute("role", "status");
      h.style.cssText = "position:fixed;left:50%;transform:translateX(-50%);bottom:90px;z-index:2147483002;max-width:90vw;box-sizing:border-box;padding:11px 15px;border-radius:13px;background:#ff1515;color:#fff;font:600 12.5px/1.4 'Instrument Sans',ui-sans-serif,system-ui,-apple-system,sans-serif;box-shadow:0 10px 34px rgba(0,0,0,0.45);text-align:center;cursor:pointer;";
      h.innerHTML = "🔊 Il browser dell'app blocca l'audio. Apri la pagina in Safari/Chrome (menu ⋯) — <u>ok</u>";
      h.addEventListener("click", removeInAppHint);
      document.body.appendChild(h);
      hintEl = h;
      setTimeout(removeInAppHint, 13000);
    }

    function minimize() {
      if (root.classList.contains("is-min")) return;
      fx.burst(shell.getBoundingClientRect(), "out");
      root.classList.add("is-min"); persist();
    }
    function restore() {
      if (!root.classList.contains("is-min")) return;
      var from = dotBtn.getBoundingClientRect(); // bloom from where the dot sits
      root.classList.remove("is-min");
      // Fresh forward pop-in + an outward spark bloom — never a reversed collapse.
      root.classList.add("is-restoring");
      setTimeout(function () { root.classList.remove("is-restoring"); }, 560);
      fx.burst(from, "out");
      persist();
    }
    if (minBtn) minBtn.addEventListener("click", minimize);
    if (dotBtn) dotBtn.addEventListener("click", restore);

    /* ---- Cinema / meditation mode: Ken Burns slideshow synced to the music - */
    cinema = (function () {
      // The VIAGGIO trigger now lives in the gallery's mode selector (moved out of the
      // player), so look it up document-wide rather than only inside the player root.
      var btn = document.querySelector("[data-mmp-cinema]");
      if (!btn) return null;
      var vtoggle = btn.closest("[data-mg-view-toggle]"); if (vtoggle) vtoggle.hidden = false; // reveal the selector even when the mosaic toggle is off
      var SLIDE_MS = 8000, FADE_MS = 2200;
      var overlay = null, scenes = null, active = 0, order = [], oi = 0, pos = 0, slideT = null, capT = null, idleT = null, fadeT = null, autoMinT = null, onKey = null, sceneSeq = 0;

      // Cap DPR at 3 (iPad Pro is 2) so we never chase absurd texture sizes.
      var DPR = Math.min(window.devicePixelRatio || 1, 3);
      function texW(cssW) {
        // Texture width for a photo DISPLAYED at cssW CSS px (already incl. the pimg
        // bleed). Sizing to the real on-screen size × DPR keeps every pixel ≥ native
        // (pristine) yet slashes the GPU working set vs the old blanket full-screen
        // width — that headroom is what lets VIAGGIO hold 120 Hz. Hard per-texture
        // ceiling bounds memory (tighter on phones); floor keeps small panels crisp.
        var vw = window.innerWidth || 1440, vh = window.innerHeight || 800;
        var cap = (Math.min(vw, vh) < 700) ? 2000 : 3200; // phone vs tablet/desktop
        // Floor kept modest so a small mosaic tile isn't forced to a heavy 1200px texture
        // (that made ~14 tiles slow to arrive → black flashes). cssW×DPR still governs crispness.
        return Math.min(cap, Math.max(680, Math.round(cssW * DPR / 100) * 100));
      }
      function sizedSrc(raw, cssW) {
        // Shopify CDN URLs carry ?width=NNNN; swap it for the right-sized value. URLs
        // without a width param (e.g. local preview images) are left untouched.
        return raw.replace(/([?&]width=)\d+/, function (m, p1) { return p1 + texW(cssW); });
      }
      function lqip(raw) {
        // Tiny placeholder that backs every tile so it is NEVER black — it shows (blurred
        // by up-scaling) until the full image decodes, and it also fills the pan bleed.
        return raw.replace(/([?&]width=)\d+/, function (m, p1) { return p1 + "220"; });
      }
      function gather() {
        var out = [], seen = {};
        document.querySelectorAll(".mg-card__img").forEach(function (im) {
          var card = im.closest("[data-mg-item]");
          if (card && card.hidden) return; // only the current album (visible cards)
          // FULL resolution: data-full lives on the lightbox trigger, NOT on the
          // <img> (whose own src is only ~800px). Keep the RAW url here; showScene
          // right-sizes each photo to the panel it lands in (see texW / sizedSrc).
          var trig = card && card.querySelector("[data-full]");
          var s = (trig && trig.getAttribute("data-full")) || im.currentSrc || im.src;
          if (!s) return;
          if (seen[s]) return; seen[s] = 1;
          var ar = parseFloat(getComputedStyle(im).getPropertyValue("--mg-ar")) ||
                   (im.naturalWidth && im.naturalHeight ? im.naturalWidth / im.naturalHeight : 1.4);
          out.push({ src: s, ar: ar }); // keep aspect for the mosaic layout
        });
        return out;
      }
      if (gather().length < 3) { btn.hidden = true; return null; } // no gallery on this page → hide

      function shuffle(a) { for (var i = a.length - 1; i > 0; i--) { var j = (Math.random() * (i + 1)) | 0, t = a[i]; a[i] = a[j]; a[j] = t; } return a; }
      function on() { return root.classList.contains("is-cinema"); }
      function geoInfo() { // the album you're "travelling" + a topographic coordinate
        var active = document.querySelector(".mg-filter.is-active");
        var name = active ? ((active.querySelector(".mg-filter__label") || active).textContent || "").trim() : "";
        var filter = active ? active.getAttribute("data-filter") : "*";
        if (filter === "*") name = (name || "").replace(/📸/g, "").trim() || "Archivio";
        var coord = "", G = [{ re: /thai|bangkok/i, c: "13.76° N · 100.50° E" }, { re: /new\s*york|nyc/i, c: "40.71° N · 74.01° W" }];
        for (var i = 0; i < G.length; i++) { if (G[i].re.test(name)) { coord = G[i].c; break; } }
        return { name: name || "MIDOLO", coord: coord };
      }
      function kenBurns(el) {
        // Translate-dominant so it stays crisp — transform:scale magnifies the
        // composited texture (blur), translate does not. A bold directional pan
        // across the oversized layer + a whisper of zoom = sharp AND dynamic.
        var ang = Math.random() * Math.PI * 2, mag = 19 + Math.random() * 6; // pan travel % (wider, still inside the -18% bleed)
        var sx = Math.cos(ang) * mag / 2, sy = Math.sin(ang) * mag / 2;
        try {
          el.animate([
            { transform: "translate(" + (-sx).toFixed(1) + "%," + (-sy).toFixed(1) + "%) scale(1)" },
            { transform: "translate(" + sx.toFixed(1) + "%," + sy.toFixed(1) + "%) scale(1.06)" }
          ], { duration: SLIDE_MS + FADE_MS + 400, easing: "linear", fill: "forwards" });
        } catch (e) { el.style.transform = "scale(1.05)"; }
      }
      function advanceProgress(n) { // thin red bar filling across the album
        if (!overlay) return;
        var bar = overlay.querySelector(".mmp-cinema__progress");
        if (!bar) return;
        pos += (n || 1);
        if (pos > order.length) { pos = pos - order.length; bar.style.transition = "none"; bar.style.width = "0%"; void bar.offsetWidth; }
        bar.style.transition = "width " + (SLIDE_MS / 1000).toFixed(1) + "s linear";
        bar.style.width = (pos / order.length * 100).toFixed(2) + "%";
      }
      function buildScene() {
        if (!order.length) return { list: [], portrait: false };
        var vw = window.innerWidth || 1440, vh = window.innerHeight || 800;
        var portrait = vh > vw; // tall screen (phone upright) → stack photos vertically
        // Tablet + desktop → one dominant image with three secondary images.
        // Phone widths retain the existing single-strip path.
        if (vw >= 750) {
          var feature = buildFeatureCinemaItems(order, oi);
          oi = feature.nextIndex;
          if (oi >= order.length) { shuffle(order); oi = 0; }
          return { feature: true, list: feature.items, portrait: portrait };
        }
        // --- single justified strip (portrait column / short-landscape row) -----------------
        var A = portrait ? (vh / vw) : (vw / vh); // long-axis ratio (always ≥ ~1)
        //   landscape row:    extent = aspect (w/h)   ·   portrait column: extent = 1/aspect (h/w)
        function ext(it) { return portrait ? (1 / it.ar) : it.ar; }
        var list = [order[oi % order.length]]; oi++;
        var sum = ext(list[0]);
        while (list.length < 4 && sum < A - 0.15) {
          var need = A - sum, bestJ = 0, bestD = 1e9;
          for (var j = 0; j < 6 && j < order.length; j++) {
            var d = Math.abs(ext(order[(oi + j) % order.length]) - need);
            if (d < bestD) { bestD = d; bestJ = j; }
          }
          var ci = (oi + bestJ) % order.length, chosen = order[ci];
          order[ci] = order[oi % order.length]; order[oi % order.length] = chosen; oi++; // consume the picked photo
          list.push(chosen); sum += ext(chosen);
        }
        if (oi >= order.length) { shuffle(order); oi = 0; } // fresh random order every full pass
        return { mosaic: false, list: list, portrait: portrait };
      }
      function showScene() {
        if (!overlay || !order.length) return;
        // Re-entrancy token. Rapid triggers (the fullscreen-entry relayout + schedule +
        // trackChanged) used to build into the SAME scene slot and BOTH reveal → active
        // flipped twice → both scenes stayed visible (the ghosting / double-exposure seen
        // on real devices, where fullscreen — hence the relayout — actually fires). A newer
        // showScene now supersedes any in-flight build: only the latest seq may reveal.
        var mySeq = ++sceneSeq;
        var res = buildScene();
        if (!res.mosaic && !(res.list && res.list.length)) return;
        overlay.classList.toggle("is-portrait", !!res.portrait);
        overlay.classList.toggle("is-mosaic", !!res.mosaic);
        overlay.classList.toggle("is-feature", !!res.feature);
        var vw = window.innerWidth || 1440, vh = window.innerHeight || 800;
        // Build the DOM + each panel's real on-screen box (used to right-size its texture), for
        // either the mosaic (rows of panels) or the single strip.
        var boxes = [], html = "", r, k;
        if (res.feature) {
          var roles = ["dominant", "secondary-1", "secondary-2", "secondary-3"];
          for (k = 0; k < res.list.length; k++) {
            html += '<div class="mmp-cinema__panel" data-feature-role="' + roles[k] + '" style="background-image:url(\'' + lqip(res.list[k].src) + '\')"><img class="mmp-cinema__pimg" alt="" decoding="async"></div>';
          }
        } else if (res.mosaic) {
          var rowH = vh / res.rows.length;
          for (r = 0; r < res.rows.length; r++) {
            var row = res.rows[r], rsum = 0;
            for (k = 0; k < row.length; k++) rsum += row[k].ar;
            html += '<div class="mmp-cinema__row">';
            for (k = 0; k < row.length; k++) {
              html += '<div class="mmp-cinema__panel" style="flex:' + row[k].ar.toFixed(3) + ";background-image:url('" + lqip(row[k].src) + "')\"><img class=\"mmp-cinema__pimg\" alt=\"\" decoding=\"async\"></div>";
              boxes.push({ p: row[k], w: (row[k].ar / rsum) * vw, h: rowH });
            }
            html += '</div>';
          }
        } else {
          var flx = res.list.map(function (p) { return res.portrait ? (1 / p.ar) : p.ar; });
          var flSum = flx.reduce(function (a, b) { return a + b; }, 0) || 1;
          for (k = 0; k < res.list.length; k++) {
            html += '<div class="mmp-cinema__panel" style="flex:' + flx[k].toFixed(3) + ";background-image:url('" + lqip(res.list[k].src) + "')\"><img class=\"mmp-cinema__pimg\" alt=\"\" decoding=\"async\"></div>";
            boxes.push({ p: res.list[k], w: res.portrait ? vw : (flx[k] / flSum) * vw, h: res.portrait ? (flx[k] / flSum) * vh : vh });
          }
        }
        var scene = scenes[active ^ 1], old = scenes[active];
        clearCinemaScene(scene);
        if (res.feature) scene.setAttribute("data-feature-count", String(res.list.length));
        else scene.removeAttribute("data-feature-count");
        scene.innerHTML = html;
        if (res.feature) {
          scene.querySelectorAll(".mmp-cinema__panel").forEach(function (panel, index) {
            var rect = panel.getBoundingClientRect();
            boxes.push({ p: res.list[index], w: rect.width, h: rect.height });
          });
        }
        if (!boxes.length) return;
        var imgs = scene.querySelectorAll(".mmp-cinema__pimg"), loaded = 0;

        function reveal() {
          if (!overlay || mySeq !== sceneSeq) return; // superseded by a newer build → don't reveal
          // Only ONE scene animates at the peak-load moment: freeze the outgoing pans and
          // release their transform layers (the outgoing scene is fading away behind the
          // opaque incoming one, so freezing it is invisible).
          try { old.querySelectorAll(".mmp-cinema__pimg").forEach(function (el) {
            el.getAnimations().forEach(function (a) { a.pause(); });
            el.style.willChange = "";
          }); } catch (e) {}
          old.style.willChange = "opacity";
          scene.style.transition = "";      // restore the CSS 0.55s crossfade (was 'none' for the warm)
          scene.style.opacity = "1";        // fade in over the still-opaque outgoing → never a black gap
          active ^= 1;
          advanceProgress(boxes.length);
          clearTimeout(fadeT);
          fadeT = setTimeout(function () {
            if (!overlay) return;
            old.style.opacity = "0";        // outgoing gone (was occluded anyway)
            old.style.willChange = "";
            scene.style.willChange = "";    // incoming fully faded in → release its opacity layer
            clearCinemaScene(old);
          }, FADE_MS + 150);
        }
        function warmThenReveal() {
          if (!overlay || mySeq !== sceneSeq) return; // superseded → leave the scene to the newer build
          scene.style.zIndex = "1"; old.style.zIndex = "0";
          scene.style.willChange = "opacity";
          // Paint the incoming scene at a hair of opacity with NO transition so its
          // textures raster + upload to the GPU on the very next frame; the crossfade
          // (a couple frames later) is then upload-free — kills the 120 Hz transition hitch.
          scene.style.transition = "none";
          scene.style.opacity = "0.02";
          void scene.offsetWidth; // flush style/layout so the 0.02 paint is committed
          requestAnimationFrame(function () { requestAnimationFrame(function () { reveal(); }); });
        }
        boxes.forEach(function (b, i) {
          var el = imgs[i], fired = false, p = b.p;
          // Real on-screen box of this tile → image width under object-fit:cover for its aspect
          // → +the -22% pimg bleed. Mosaic tiles are small, so each serves a light texture.
          var imgCssW = Math.max(b.w, b.h * p.ar) * 1.44;
          function done() {
            if (fired || !overlay || mySeq !== sceneSeq) return; fired = true; // ignore a superseded build's late decodes
            el.style.willChange = "transform";
            kenBurns(el);
            if (++loaded === boxes.length) warmThenReveal(); // reveal once EVERY tile is decoded
          }
          el.onerror = done;
          el.src = sizedSrc(p.src, imgCssW);
          // Decode on the SAME element that composites (no second background-image decode
          // path), off the main thread. Timeout guards a decode that never settles
          // (e.g. a backgrounded tab) so VIAGGIO can't freeze.
          if (el.decode) {
            var t = setTimeout(done, 4000);
            el.decode().then(function () { clearTimeout(t); done(); }, function () { clearTimeout(t); done(); });
          } else { el.onload = done; }
        });
      }
      function schedule() { clearTimeout(slideT); slideT = setTimeout(function () { showScene(); schedule(); }, SLIDE_MS); }
      function caption() {
        if (!overlay) return;
        var cap = overlay.querySelector(".mmp-cinema__cap"), d = player && player.getVideoData ? player.getVideoData() : null;
        if (d && d.title) { cap.querySelector("span").textContent = d.title; cap.classList.add("is-on"); clearTimeout(capT); capT = setTimeout(function () { if (overlay) cap.classList.remove("is-on"); }, 5200); }
      }
      function revealUI() { if (!overlay) return; overlay.classList.add("is-ui"); clearTimeout(idleT); idleT = setTimeout(function () { if (overlay) overlay.classList.remove("is-ui"); }, 3200); }
      function build() {
        overlay = document.createElement("div");
        overlay.className = "mmp-cinema";
        overlay.innerHTML =
          '<div class="mmp-cinema__scene"></div><div class="mmp-cinema__scene"></div>' +
          '<div class="mmp-cinema__vignette"></div>' +
          '<div class="mmp-cinema__progress"></div>' +
          '<div class="mmp-cinema__geo"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="8"/><path d="M12 1.5v3.5M12 19v3.5M1.5 12H5M19 12h3.5"/><circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none"/></svg><span class="mmp-cinema__geo-lines"><span class="mmp-cinema__geo-name"></span><span class="mmp-cinema__geo-coord"></span></span></div>' +
          '<button class="mmp-cinema__share" data-c="share" aria-label="Condividi il VIAGGIO"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 14V3M12 3 8.5 6.5M12 3l3.5 3.5M5 11v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8"/></svg></button>' +
          '<button class="mmp-cinema__close" data-c="exit" aria-label="Esci dal VIAGGIO"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
        document.body.appendChild(overlay);
        scenes = overlay.querySelectorAll(".mmp-cinema__scene");
        overlay.addEventListener("click", function (e) {
          if (e.target.closest("[data-c=exit]")) exit();
          // Share the VIAGGIO experience — the gallery builds the link + native share sheet.
          else if (e.target.closest("[data-c=share]")) document.dispatchEvent(new CustomEvent("mg:share-viaggio"));
        });
      }
      // Il layout della scena è calcolato da window.innerWidth/innerHeight. Sul telefono il
      // fullscreen (e le rotazioni) cambiano la viewport DOPO l'apertura → senza ricalcolo la scena
      // resta dimensionata per la viewport vecchia (foto croppate / fascia nera a destra).
      // Ricalcola su fullscreenchange/resize/orientation, con debounce per collassare i burst
      // della transizione fullscreen.
      var relayoutT;
      function onViewportChange() { clearTimeout(relayoutT); relayoutT = setTimeout(function () { if (on()) showScene(); }, 160); }
      function enter() {
        var pool = gather(); if (pool.length < 3) return;
        order = shuffle(pool.slice()); oi = 0; active = 0; pos = 0;
        build();
        var g = geoInfo();
        overlay.querySelector(".mmp-cinema__geo-name").textContent = g.name;
        overlay.querySelector(".mmp-cinema__geo-coord").textContent = g.coord;
        if (!g.coord) overlay.querySelector(".mmp-cinema__geo").classList.add("no-coord");
        root.classList.remove("is-min"); // show the FULL player as the control surface first…
        root.classList.add("is-cinema");
        root.classList.add("is-open"); // …expanded + on top (auto-minimises after 5s, see below)
        btn.setAttribute("aria-pressed", "true");
        document.documentElement.classList.add("mmp-cinema-lock");
        if (!startedOnce) bootPlayer(function () { startPlayback(); });
        else if (!playing) startPlayback();
        showScene(); schedule();
        try { var r = document.documentElement.requestFullscreen; if (r) r.call(document.documentElement).catch(function () {}); } catch (e) {}
        document.addEventListener("fullscreenchange", onViewportChange);
        window.addEventListener("resize", onViewportChange);
        window.addEventListener("orientationchange", onViewportChange);
        onKey = function (e) {
          if (e.key === "Escape") exit();
          else if (e.key === "ArrowRight" && player && player.nextVideo) player.nextVideo();
          else if (e.key === "ArrowLeft" && player && player.previousVideo) player.previousVideo();
        };
        document.addEventListener("keydown", onKey);
        requestAnimationFrame(function () { if (overlay) overlay.classList.add("is-open"); });
        // After a few seconds of VIAGGIO, tuck the player away with its cute minimise
        // burst so the experience goes fully immersive. The corner ✕ still exits and
        // the restore dot still brings playback controls back.
        clearTimeout(autoMinT);
        autoMinT = setTimeout(function () { if (on() && !root.classList.contains("is-min")) minimize(); }, 5000);
      }
      function exit() {
        clearTimeout(slideT); clearTimeout(capT); clearTimeout(idleT); clearTimeout(fadeT); clearTimeout(autoMinT); clearTimeout(relayoutT);
        sceneSeq++;
        if (onKey) document.removeEventListener("keydown", onKey);
        document.removeEventListener("fullscreenchange", onViewportChange);
        window.removeEventListener("resize", onViewportChange);
        window.removeEventListener("orientationchange", onViewportChange);
        root.classList.remove("is-cinema");
        btn.setAttribute("aria-pressed", "false");
        document.documentElement.classList.remove("mmp-cinema-lock");
        try { if (document.fullscreenElement) document.exitFullscreen(); } catch (e) {}
        if (scenes) Array.prototype.forEach.call(scenes, clearCinemaScene);
        if (overlay) { var o = overlay; overlay = null; o.classList.remove("is-open"); setTimeout(function () { if (o.parentNode) o.parentNode.removeChild(o); }, 520); }
      }
      btn.addEventListener("click", function () { if (on()) exit(); else enter(); });
      return { trackChanged: function () { if (on()) { showScene(); schedule(); } } };
    })();

    window.addEventListener("pagehide", persist);
    window.addEventListener("beforeunload", persist);
    var rzT; window.addEventListener("resize", function () { clearTimeout(rzT); rzT = setTimeout(measureTitle, 200); });
  }

  /* ---------- WebGPU particle FX (graceful no-op fallback) ---------------- */
  function createFx(canvas) {
    var noop = { burst: function () {} };
    if (REDUCED || !canvas || !navigator.gpu) return noop;

    var device = null, ctx = null, pipeline = null, quadBuf = null, uBuf = null, partBuf = null;
    var MAX = 300, ready = false, running = false;
    var api = { burst: function () {} }; // no-op until GPU is ready

    var WGSL = [
      "struct U { res:vec2f, time:f32, mode:f32, origin:vec2f, size:f32, _p:f32 };",
      "@group(0) @binding(0) var<uniform> u:U;",
      "struct P { off:vec2f, vel:vec2f, seed:f32, _q:f32 };",
      "@group(0) @binding(1) var<storage, read> parts:array<P>;",
      "struct Vo { @builtin(position) pos:vec4f, @location(0) uv:vec2f, @location(1) a:f32 };",
      "@vertex fn vs(@location(0) corner:vec2f, @builtin(instance_index) i:u32) -> Vo {",
      "  let p = parts[i];",
      "  let prog = select(u.time, 1.0 - u.time, u.mode > 0.5);",
      "  let e = 1.0 - pow(1.0 - prog, 3.0);",
      "  let center = u.origin + p.off + p.vel * e;",
      "  let a = select(1.0 - u.time, u.time, u.mode > 0.5);",
      "  let px = center + corner * u.size * (0.6 + p.seed * 0.8);",
      "  let clip = vec2f(px.x / u.res.x * 2.0 - 1.0, 1.0 - px.y / u.res.y * 2.0);",
      "  var o:Vo; o.pos = vec4f(clip, 0.0, 1.0); o.uv = corner; o.a = a; return o;",
      "}",
      "@fragment fn fs(v:Vo) -> @location(0) vec4f {",
      "  let m = smoothstep(1.0, 0.0, length(v.uv)) * v.a;",
      "  let col = vec3f(1.0, 0.09, 0.09);",
      "  return vec4f(col * m, m);",
      "}"
    ].join("\n");

    (async function init() {
      try {
        var adapter = await navigator.gpu.requestAdapter();
        if (!adapter) return;
        device = await adapter.requestDevice();
        ctx = canvas.getContext("webgpu");
        if (!ctx) return;
        var format = navigator.gpu.getPreferredCanvasFormat();
        ctx.configure({ device: device, format: format, alphaMode: "premultiplied" });
        var mod = device.createShaderModule({ code: WGSL });
        pipeline = device.createRenderPipeline({
          layout: "auto",
          vertex: { module: mod, entryPoint: "vs", buffers: [{ arrayStride: 8, attributes: [{ shaderLocation: 0, offset: 0, format: "float32x2" }] }] },
          fragment: { module: mod, entryPoint: "fs", targets: [{ format: format, blend: { color: { srcFactor: "one", dstFactor: "one" }, alpha: { srcFactor: "one", dstFactor: "one" } } }] },
          primitive: { topology: "triangle-list" }
        });
        var quad = new Float32Array([-1,-1, 1,-1, 1,1, -1,-1, 1,1, -1,1]);
        quadBuf = device.createBuffer({ size: quad.byteLength, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });
        device.queue.writeBuffer(quadBuf, 0, quad);
        uBuf = device.createBuffer({ size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
        partBuf = device.createBuffer({ size: MAX * 24, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
        ready = true;
        api.burst = gpuBurst;
      } catch (e) { /* stay on no-op */ }
    })();

    function gpuBurst(rect, mode) {
      if (!ready || running) return;
      running = true;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      var cx = (rect.left + rect.width / 2) * dpr;
      var cy = (rect.top + rect.height / 2) * dpr;
      var data = new Float32Array(MAX * 6);
      for (var i = 0; i < MAX; i++) {
        var ang = Math.random() * Math.PI * 2;
        var spd = (40 + Math.random() * 150) * dpr;
        var ox = (Math.random() - 0.5) * rect.width * dpr;
        var oy = (Math.random() - 0.5) * rect.height * dpr;
        data[i*6+0] = ox; data[i*6+1] = oy;
        data[i*6+2] = Math.cos(ang) * spd;
        data[i*6+3] = Math.sin(ang) * spd - 40 * dpr; // slight upward drift
        data[i*6+4] = Math.random(); data[i*6+5] = 0;
      }
      device.queue.writeBuffer(partBuf, 0, data);
      var bind = device.createBindGroup({ layout: pipeline.getBindGroupLayout(0), entries: [
        { binding: 0, resource: { buffer: uBuf } },
        { binding: 1, resource: { buffer: partBuf } }
      ]});
      var size = Math.max(6, Math.min(rect.width, 26)) * dpr;
      root_addFx(true);
      var start = performance.now(), DUR = 720;
      // Safety net: if rAF stalls (e.g. tab backgrounded mid-burst), never leave
      // the FX overlay stuck on or the controller locked.
      var safety = setTimeout(function () { running = false; root_addFx(false); }, 1800);
      function frame(now) {
        var t = Math.min(1, (now - start) / DUR);
        var u = new Float32Array([canvas.width, canvas.height, t, mode === "in" ? 1 : 0, cx, cy, size, 0]);
        device.queue.writeBuffer(uBuf, 0, u);
        var enc = device.createCommandEncoder();
        var pass = enc.beginRenderPass({ colorAttachments: [{ view: ctx.getCurrentTexture().createView(), clearValue: { r:0,g:0,b:0,a:0 }, loadOp: "clear", storeOp: "store" }] });
        pass.setPipeline(pipeline); pass.setBindGroup(0, bind); pass.setVertexBuffer(0, quadBuf);
        pass.draw(6, MAX); pass.end();
        device.queue.submit([enc.finish()]);
        if (t < 1) { requestAnimationFrame(frame); }
        else { clearTimeout(safety); running = false; root_addFx(false); }
      }
      requestAnimationFrame(frame);
    }
    function root_addFx(on) {
      var el = canvas.closest(".mmp");
      if (el) el.classList.toggle("is-fx", on);
    }
    return api;
  }

  /* ---------- Boot -------------------------------------------------------- */
  function boot() { var el = document.querySelector("[data-mmp]"); if (el && !el.__mmp) { el.__mmp = true; initPlayer(el); } }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
